/**
 * Arrondis (border-radius) de la plateforme : des pixels ENTIERS réglés par
 * l'exploitant, posés sur les jetons `--radius-*` de `assets/main.css`.
 *
 * Fonctions PURES, comme `palette.ts` — l'application au document vit dans
 * `appliquer.ts`. Fichier à part : les couleurs dépendent du thème et ont des
 * déclinaisons calculées ; un arrondi n'a ni l'un ni l'autre.
 */

/**
 * Jetons réglables. MIROIR de `ARRONDIS_REGLABLES` (backend
 * `core/apparence/schemas.ts`) et de la migration 0045 — un test backend fige
 * les trois. `--radius-xl` et `--radius-2xl` n'y sont pas : aucun composant ne
 * les emploie de façon visible, les proposer serait un réglage sans effet.
 * `--radius-full` non plus : c'est la forme « pastille », pas un arrondi.
 */
export const ARRONDIS_REGLABLES = ['xs', 'sm', 'md', 'lg'] as const
export type ArrondiReglable = (typeof ARRONDIS_REGLABLES)[number]

export type ArrondisPlateforme = Partial<Record<ArrondiReglable, number>>

/** Bornes en pixels, les mêmes que le serveur et la base (test de contrat). */
export const ARRONDI_MIN = 0
export const ARRONDI_MAX = 32

/**
 * Valeurs de `assets/main.css` (couche ADS, la dernière déclarée). Répétées
 * pour la même raison que `DEFAUTS` de `palette.ts`, et vérifiées par le même
 * type de test.
 */
export const DEFAUTS_ARRONDIS: Readonly<Record<ArrondiReglable, number>> = {
  xs: 3,
  sm: 3,
  md: 4,
  lg: 8,
}

/** Entier de pixels dans les bornes — la seule forme qui entre dans la feuille. */
export function estArrondi(valeur: unknown): valeur is number {
  return (
    typeof valeur === 'number' &&
    Number.isInteger(valeur) &&
    valeur >= ARRONDI_MIN &&
    valeur <= ARRONDI_MAX
  )
}

/** Valide une forme reçue et la recopie, ou `null` si UN élément est hors contrat. */
export function normaliserArrondis(brut: unknown): ArrondisPlateforme | null {
  if (typeof brut !== 'object' || brut === null || Array.isArray(brut)) return null
  const resultat: ArrondisPlateforme = {}
  for (const [cle, valeur] of Object.entries(brut)) {
    if (!(ARRONDIS_REGLABLES as readonly string[]).includes(cle)) return null
    if (!estArrondi(valeur)) return null
    resultat[cle as ArrondiReglable] = valeur
  }
  return resultat
}

/**
 * Les arrondis suivent-ils l'échelle (xs ≤ sm ≤ md ≤ lg) ? Pas une règle
 * imposée — un choix délibéré reste possible —, mais une inversion fait qu'un
 * badge est plus arrondi que le bouton qui le contient : l'écran la signale.
 */
export function suitEchelle(effectifs: Readonly<Record<ArrondiReglable, number>>): boolean {
  return ARRONDIS_REGLABLES.every(
    (cle, i) => i === 0 || effectifs[ARRONDIS_REGLABLES[i - 1]!] <= effectifs[cle],
  )
}

/** Jetons CSS des arrondis donnés (rien pour un arrondi non réglé). */
export function jetonsArrondis(arrondis: ArrondisPlateforme): Record<string, string> {
  const jetons: Record<string, string> = {}
  for (const cle of ARRONDIS_REGLABLES) {
    const valeur = arrondis[cle]
    if (valeur === undefined) continue
    if (!estArrondi(valeur)) throw new Error(`Arrondi hors contrat : ${JSON.stringify(valeur)}`)
    jetons[`--radius-${cle}`] = `${valeur}px`
  }
  return jetons
}

/**
 * `html:root`, spécificité (0,1,1) : l'emporte sur les deux `:root` de
 * `main.css` (0,1,0) quel que soit l'ordre d'insertion des feuilles. Le
 * contraste renforcé n'est PAS exclu, contrairement aux couleurs : il ne touche
 * pas aux arrondis.
 */
export const SELECTEUR_ARRONDIS = 'html:root'

/** Feuille des arrondis, `''` sans surcharge. Lève sur une valeur hors contrat. */
export function feuilleArrondis(arrondis: ArrondisPlateforme): string {
  const lignes = Object.entries(jetonsArrondis(arrondis)).map(([n, v]) => `  ${n}: ${v};`)
  return lignes.length === 0 ? '' : `${SELECTEUR_ARRONDIS} {\n${lignes.join('\n')}\n}`
}
